import React from "react";
import SEO from "../../SEO";
/* Reusable Component for cleaner code */
const FeatureItem = ({ title, desc }) => (
  <div className="expertise-item">
    <span className="icon">✓</span>
    <div>
      <h5>{title}</h5>
      <p>{desc}</p>
    </div>
  </div>
);
const majorModules = [
  {
    title: "Dashboard",
    icon: "bi-speedometer2",
    description:
      "View KPIs, sales trends, and inventory status instantly. Make data-driven decisions with real-time insights.",
    img: process.env.PUBLIC_URL + "/img/RestaurantInventoryApp/Dashboard.png",
  },
  {
    title: "Restaurant POS",
    icon: "bi-shop-window",
    description:
      "Fast and reliable POS system for restaurants. Integrated with inventory and accounting modules.",
    img:
      process.env.PUBLIC_URL + "/img/RestaurantInventoryApp/RestaurantPOS.png",
  },
  {
    title: "Kitchen Display System",
    icon: "bi-display",
    description:
      "Real-time kitchen display for order management and tracking. Improve kitchen efficiency.",
    img:
      process.env.PUBLIC_URL +
      "/img/RestaurantInventoryApp/KitchenDisplaySystem.png",
  },
  {
    title: "Recipe Maker",
    icon: "bi-book",
    description:
      "Create and manage recipes with detailed ingredients. Ensure consistency and quality in food preparation.",
    img:
      process.env.PUBLIC_URL + "/img/RestaurantInventoryApp/ReceipeMaker.png",
  },
  {
    title: "Chart Of Account",
    icon: "bi-graph-up",
    description:
      "Define and manage your financial account structure. Ensure organized and accurate accounting.",
    img:
      process.env.PUBLIC_URL + "/img/RestaurantInventoryApp/ChartOfAccount.png",
  },
  {
    title: "Trail Balance",
    icon: "bi-journal-check",
    description:
      "View trial balance reports for financial accuracy. Ensure balanced accounts and audit readiness.",
    img:
      process.env.PUBLIC_URL + "/img/RestaurantInventoryApp/TrailBalance.png",
  },
];

const otherFeatures = [
  "Category Management",
  "Customer Tracking",
  "Product & Stock Control",
  "Supplier & Vendor Management",
  "Bank Account Tracking",
  "Expense Management",
  "Journal & Payment Vouchers",
  "Role-based Permissions",
  "Product Barcode Generation",
  "Supplier Payments",
  "User Access Panel",
  "Vendor Settlement",
  "Account Reconciliation",
];

function EnterpriseERP() {
  const lastRowThreshold =
    otherFeatures.length > 10
      ? otherFeatures.length - (otherFeatures.length % 10 || 10)
      : 0;
  const initialFeatures = otherFeatures.slice(0, lastRowThreshold);
  const lastFeatures = otherFeatures.slice(lastRowThreshold);
  return (
    <>
      <SEO
        title="ERP Software for Restaurants & Businesses in Pakistan | MZR Software"
        description="Complete ERP software for restaurants and businesses including POS, inventory, accounting, and reporting system."
        url="/erp-software"
      />
      <div className="portfolio-section">
        <div className="container">
          {/* Header */}
          <div className="section-header text-center mb-5">
            <h1 className="section-title">
              MZR Software{" "}
              <span className="highlight">
                {" "}
                ERP Software for Restaurants & Businesses in Pakistan
              </span>
            </h1>
            <p className="section-subtitle">
              Complete ERP software including POS system, inventory management,
              accounting, reporting and business automation solutions for
              restaurants and companies in Pakistan.
            </p>
          </div>

          {/* Video Demo */}
          <div className="text-center mb-5">
            <iframe
              width="900"
              height="500"
              src="https://www.youtube.com/embed/6unp9xeoQMQ"
              title="ERP Demo"
              frameBorder="0"
              allowFullScreen
              style={{
                maxWidth: "100%",
                borderRadius: "15px",
                boxShadow: "0 10px 30px rgba(0,212,255,0.2)",
              }}
            ></iframe>
          </div>

          {/* Major Modules Grid */}
          <h3 className="text-center mb-5 text-white fw-bold">
            Core System Modules
          </h3>
          <div className="portfolio-grid">
            {majorModules.map((module, index) => (
              <div key={index} className="portfolio-card">
                <div className="card-image-wrapper">
                  <img
                    src={module.img}
                    alt={`ERP software ${module.title} system`}
                    className="project-video"
                  />
                  <div className="card-overlay">
                    <i
                      className={`bi ${module.icon} project-icon text-info`}
                    ></i>
                  </div>
                </div>
                <div className="card-content">
                  <h5 className="card-title">{module.title}</h5>
                  <p className="card-description">{module.description}</p>
                  <div className="card-category">
                    <span className="category-badge">Major Module</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Full-Width Rows (10 points each row: 5 Left, 5 Right) */}
          {Array.from({ length: initialFeatures.length / 10 }).map(
            (_, rowIndex) => (
              <div className="row g-4 mb-4" key={`full-row-${rowIndex}`}>
                <div className="col-md-6">
                  <div className="expertise-list">
                    {initialFeatures
                      .slice(rowIndex * 10, rowIndex * 10 + 5)
                      .map((f, i) => (
                        <FeatureItem
                          key={i}
                          title={f}
                          desc="Seamless data flow integration."
                        />
                      ))}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="expertise-list">
                    {initialFeatures
                      .slice(rowIndex * 10 + 5, rowIndex * 10 + 10)
                      .map((f, i) => (
                        <FeatureItem
                          key={i}
                          title={f}
                          desc="Core ERP engine synchronization."
                        />
                      ))}
                  </div>
                </div>
              </div>
            ),
          )}

          {/* Last Row: 2 Columns of Features (8-cols wide) + Stats Grid (4-cols wide) */}
          <div className="row g-4 align-items-stretch">
            <div className="col-lg-6">
              <h3 className="expertise-title mb-4 text-white">
                Additional Capabilities
              </h3>
              <div className="row">
                <div className="col-md-12">
                  <div className="expertise-list">
                    {lastFeatures.slice(0, 5).map((f, i) => (
                      <FeatureItem
                        key={i}
                        title={f}
                        desc="Advanced system module."
                      />
                    ))}
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="expertise-list">
                    {lastFeatures.slice(5).map((f, i) => (
                      <FeatureItem
                        key={i}
                        title={f}
                        desc="Integration & Support."
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Grid aligned with the LAST set of features */}
            <div className="col-lg-4">
              <div className="stats-grid h-100 d-flex flex-column justify-content-between">
                <div className="stat-box">
                  <span className="stat-value">100%</span>
                  <span className="stat-text">Offline Capable</span>
                </div>
                <div className="stat-box">
                  <span className="stat-value">Real-time</span>
                  <span className="stat-text">Sync Tech</span>
                </div>
                <div className="stat-box">
                  <span className="stat-value">24/7</span>
                  <span className="stat-text">Support</span>
                </div>
                <div className="stat-box">
                  <span className="stat-value">Secure</span>
                  <span className="stat-text">Encryption</span>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-5 text-center text-white">
            <h2>Best ERP Software in Pakistan for Business Management</h2>
            <p style={{ maxWidth: "800px", margin: "0 auto" }}>
              Our ERP software helps businesses manage inventory, POS billing,
              accounting, reports and operations in one system. Designed for
              restaurants, retail stores and enterprises looking for scalable
              and efficient business solutions.
            </p>
          </div>
          {/* CTA */}
          <div className="portfolio-cta text-center mt-5 pt-4">
            <p className="cta-text">
              Start your ERP software project today and automate your business
              operations.
            </p>

            <a
              href="https://wa.me/923351206586?text=Hi%20I%20need%20ERP%20software"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              🚀 Get Free Consultation on WhatsApp
            </a>
          </div>
        </div>
      </div>
    </>
  );
}

export default EnterpriseERP;
